import React, { useState } from 'react';
import CalcShell from '../CalcShell';
import { CNetRow, CNetActions, CNetResult, CNetHintBar, G } from '../other/_cnet';

// z-scores for common CI levels
const Z = { 0.80: 1.28, 0.85: 1.44, 0.90: 1.645, 0.95: 1.96, 0.99: 2.576, 0.999: 3.291 };

function SampleForProportion() {
  const [ci, setCi] = useState(0.95);
  const [moe, setMoe] = useState(0.05);
  const [p, setP] = useState(0.5);
  const [pop, setPop] = useState('');
  const [res, setRes] = useState(null);
  const calc = () => {
    const z = Z[Number(ci)] || 1.96;
    const _p = Number(p), _moe = Number(moe);
    const nInf = (z * z * _p * (1 - _p)) / (_moe * _moe);
    let nFin = nInf;
    if (pop !== '' && !isNaN(+pop)) {
      const N = Number(pop);
      nFin = nInf / (1 + (nInf - 1) / N);
    }
    setRes({ z, nInf, nFin, hasPop: pop !== '' });
  };
  const clear = () => { setCi(0.95); setMoe(0.05); setP(0.5); setPop(''); setRes(null); };
  return (
    <section>
      <h3 className="cnet-section-title">Find Out The Sample Size</h3>
      <p className="text-sm text-slate-700 mb-3">This calculator gives out the number of sampled cases needed to have a certain confidence and margin of error.</p>
      <div className="cnet-panel">
        <div className="cnet-row-2col"><div className="cnet-label">Confidence Level</div>
          <select className="cnet-select" value={ci} onChange={e => setCi(e.target.value)}>
            {Object.keys(Z).map(k => <option key={k} value={k}>{(Number(k) * 100).toFixed(1)}%</option>)}
          </select>
        </div>
        <CNetRow label="Margin of Error"><input data-testid="ss-moe" className="cnet-input" type="number" step="0.001" value={moe} onChange={e => setMoe(e.target.value)} /><span>(e.g. 0.05 = ±5%)</span></CNetRow>
        <CNetRow label="Population Proportion"><input data-testid="ss-p" className="cnet-input" type="number" step="0.01" value={p} onChange={e => setP(e.target.value)} /><span>(use 0.5 if unknown)</span></CNetRow>
        <CNetRow label="Population Size"><input data-testid="ss-pop" className="cnet-input" type="number" value={pop} onChange={e => setPop(e.target.value)} placeholder="Leave blank for infinite population" /><span>(optional)</span></CNetRow>
        <CNetActions onCalc={calc} onClear={clear} testId="ss" />
      </div>
      {res && (
        <CNetResult>
          <div className="text-2xl font-semibold text-slate-900" data-testid="ss-result">
            Sample Size: <span className="text-[#4c7b25]">{Math.ceil(res.nFin)}</span>
          </div>
          <div className="mt-2 font-mono text-sm leading-7">
            <div>n = (z² · p · (1 − p)) / e²</div>
            <div>= ({res.z}² × p × (1 − p)) / (moe)²</div>
            <div>= <b>{G(res.nInf, 2)}</b> (unbounded population)</div>
            {res.hasPop && <div>Finite population correction → <b className="text-[#4c7b25]">{Math.ceil(res.nFin)}</b> respondents</div>}
          </div>
        </CNetResult>
      )}
    </section>
  );
}

function MarginOfError() {
  const [ci, setCi] = useState(0.95);
  const [n, setN] = useState(500);
  const [p, setP] = useState(0.5);
  const [res, setRes] = useState(null);
  const calc = () => {
    const z = Z[Number(ci)] || 1.96;
    const moe = z * Math.sqrt(Number(p) * (1 - Number(p)) / Number(n));
    setRes({ z, moe });
  };
  const clear = () => { setCi(0.95); setN(500); setP(0.5); setRes(null); };
  return (
    <section className="mt-4">
      <h3 className="cnet-section-title">Find Out The Margin of Error</h3>
      <p className="text-sm text-slate-700 mb-3">This calculator gives out the margin of error or confidence interval of observation or survey.</p>
      <div className="cnet-panel">
        <div className="cnet-row-2col"><div className="cnet-label">Confidence Level</div>
          <select className="cnet-select" value={ci} onChange={e => setCi(e.target.value)}>
            {Object.keys(Z).map(k => <option key={k} value={k}>{(Number(k) * 100).toFixed(1)}%</option>)}
          </select>
        </div>
        <CNetRow label="Sample Size"><input className="cnet-input" type="number" value={n} onChange={e => setN(e.target.value)} /><span /></CNetRow>
        <CNetRow label="Population Proportion"><input className="cnet-input" type="number" step="0.01" value={p} onChange={e => setP(e.target.value)} /><span>(use 0.5 if unknown)</span></CNetRow>
        <CNetActions onCalc={calc} onClear={clear} testId="ss-moe" />
      </div>
      {res && (
        <CNetResult>
          <div className="text-2xl font-semibold text-slate-900" data-testid="ss-moe-result">
            Margin of Error: <span className="text-[#4c7b25]">±{(res.moe * 100).toFixed(2)}%</span>
          </div>
          <div className="mt-2 font-mono text-sm">e = z · √(p(1−p)/n) = {res.z} · √({G(Number(p) * (1 - Number(p)) / Number(n), 6)}) = <b>{res.moe.toFixed(6)}</b></div>
        </CNetResult>
      )}
    </section>
  );
}

export default function SampleSizeCalc() {
  return (
    <CalcShell showActions={false} look="cnet" title="Sample Size Calculator" subtitle="Compute the sample size required for a survey, or the margin of error for a given sample.">
      <CNetHintBar />
      <SampleForProportion />
      <MarginOfError />
    </CalcShell>
  );
}
